import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TombOfAkhenatenGameConfig} from "../../TombOfAkhenatenGameConfig";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {TOAGameEvent} from "../../events/TOAGameEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {Linear, TweenLite} from "gsap";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {TombOfAkhenatenTextStyles} from "../../config/TombOfAkhenatenTextStyles";
import {TombOfAkhenatenParsedGameData} from "../../server/data/TombOfAkhenatenParsedGameData";
import {TombOfAkhenatenParsedInitData} from "../../server/data/TombOfAkhenatenParsedInitData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SoundManager} from "../../sound/SoundManager";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";

export class StonePressView extends BaseView {

    private _sphinxStone: PIXI.Sprite;
    private _pulleyLayer: ScalableLayer;
    private _sphinxLayer: ScalableLayer;
    private _gameData: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData;
    private _stonePressDuration: number;
    private _stoneLiftDuration: number;
    private _stonePressStartDelay: number;
    private _stoneLiftStartDelay: number;
    private _deviceOrientation: string = Orientation.PORTRAIT;

    private PULLEY: string = "pulley";
    private ROPE_TOP: string = "ropeTop";
    private ROPE_BOTTOM: string = "ropeBottom";
    private ROPE_ATTACHED_LEFT: string = "ropeAttachedLeft";
    private ROPE_ATTACHED_RIGHT: string = "ropeAttachedRight";
    private PULLEY_CONT_LEFT: string = "leftPulleyContainer";
    private PULLEY_CONT_RIGHT: string = "rightPulleyContainer";

    private ropeTextureTop: PIXI.Texture;
    private ropeTextureBottom: PIXI.Texture;
    private ropeTextureAttached: PIXI.Texture;
    private pulleyTexture: PIXI.Texture;
    private _pulleyContainerLeft: PIXI.Container = new PIXI.Container;
    private _pulleyContainerRight: PIXI.Container = new PIXI.Container;

    private _stoneFlakeContainer: PIXI.Container = new PIXI.Container;
    private _animOnSphinxContainer: PIXI.Container = new PIXI.Container;

    private _fsLeftPortrait: PIXI.Sprite;
    private _fsLeftContainer: PIXI.Container;
    private _fsLeftText: BitmapCountUp;
    private _gameMode: string;

    public abortAnimations(): void {
        this._sphinxLayer.children.forEach((item: any) => {
            if (item.name && item.name === "sphinxAnimation") {
                (item as PIXI.Container).children.forEach((animContainer: any) => {
                    if (animContainer.name && animContainer.name === "smokeAnimations") {
                        (<TimelineSprite[]>(animContainer as PIXI.Container).children).forEach((sprite: TimelineSprite) => {
                            sprite.stopLoop();
                            sprite.hide();
                        });
                    }
                });
            }
        });

        this._pulleyLayer.children.forEach((item: any) => {
            if (item.name && item.name === "bottomStoneAnimation") {
                (<TimelineSprite[]>(item as PIXI.Container).children).forEach((sprite: TimelineSprite) => {
                    sprite.stopLoop();
                    sprite.hide();
                });
            }
        });
    }

    protected addEventListeners(): void {
        EventHandler.addEventListener(this, TOAGameEvent.ON_STONE_PRESS, (event: GameEvent) => this.onStonePress(event.params[0]));
        EventHandler.addEventListener(this, TOAGameEvent.ON_NEW_GAME_ROUND, () => this.onNewRound());
        EventHandler.addEventListener(this, GameModeEvent.CHANGE_MODE, (event: GameModeEvent) => this.onModeChange(event.params[0].newMode));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onDataParsed(event.params[0]));
        EventHandler.addEventListener(this, TOAGameEvent.SHOW_AWARDED_SPINS, () => this.addFreeSpins());
        EventHandler.addEventListener(this, TOAGameEvent.UPDATE_SPINS_LEFT_METER, (event: GameEvent) => this.updateSpinsLeft(event.params[0]));
    }

    private addFreeSpins(): void{
        if(TombOfAkhenatenGameConfig.instance.BONUS_GAME_MODES.indexOf(this._gameData.mode) > -1){
            let updatedFreeSpinCount: number = parseInt(this._fsLeftText.text) + 1;
            this.updateSpinsLeft(updatedFreeSpinCount);
            SlotGame.keypad.setZeroBetSpinCounter(updatedFreeSpinCount);
        }
    }

    protected initAnimations(): void {
        this._stonePressDuration = TombOfAkhenatenGameConfig.instance.STONE_PRESS_DURATION;
        this._stoneLiftDuration = TombOfAkhenatenGameConfig.instance.STONE_LIFT_DURATION;
        this._stonePressStartDelay = TombOfAkhenatenGameConfig.instance.STONE_PRESS_START_DELAY;
        this._stoneLiftStartDelay = TombOfAkhenatenGameConfig.instance.STONE_LIFT_START_DELAY;

        this._pulleyLayer = StageManager.getLayer(TombOfAkhenatenGameConfig.instance.LAYERS.REEL_FOREGROUND.name);
        this._sphinxLayer = StageManager.getLayer(TombOfAkhenatenGameConfig.instance.LAYERS.STONE_PRESSER.name);

        this._stoneFlakeContainer.name = "bottomStoneAnimation";
        this._animOnSphinxContainer.name = "sphinxAnimation";
        this._animOnSphinxContainer.scale.set(1, -1);
        this._animOnSphinxContainer.position.set(0, 300);

        this._sphinxStone = new PIXI.Sprite(GameResources.getTextures("stonePresser")[0]);
        this._sphinxStone.anchor.set(0.5);
        this._sphinxStone.name = "sphinxStone";
        this._sphinxStone.position.set((TombOfAkhenatenGameConfig.instance.REEL_AREA_WIDTH / 2), -82);

        this.ropeTextureTop = GameResources.getTextures("ropeTop")[0];
        this.ropeTextureBottom = GameResources.getTextures("ropeBottom")[0];
        this.pulleyTexture = GameResources.getTextures("pulley")[0];

        this.ropeTextureAttached = GameResources.getTextures("ropeAttached")[0];

        let attachedRopeLeft: PIXI.Sprite = new PIXI.Sprite(this.ropeTextureAttached);
        attachedRopeLeft.name = this.ROPE_ATTACHED_LEFT;
        attachedRopeLeft.anchor.set(0.5);
        attachedRopeLeft.position.set(70, -150);

        let attachedRopeRight: PIXI.Sprite = new PIXI.Sprite(this.ropeTextureAttached);
        attachedRopeRight.name = this.ROPE_ATTACHED_RIGHT;
        attachedRopeRight.anchor.set(0.5);
        attachedRopeRight.scale.set(-1, 1);
        attachedRopeRight.position.set(650, -150);

        this._fsLeftPortrait = new PIXI.Sprite(GameResources.getTextures("fsLeftPortrait")[0]);
        this._fsLeftPortrait.name = "fsLeftPortrait";
        this._fsLeftPortrait.anchor.set(0.5);
        this._fsLeftPortrait.position.set(55, -55);

        this._fsLeftText = new BitmapCountUp({style: TombOfAkhenatenTextStyles.BONUS_GAME_MULTIPLIER});
        this._fsLeftText.pivot.set((this._fsLeftText.width / this._fsLeftText.text.length), this._fsLeftText.height / 2);
        this._fsLeftText.name = "fsLeftText";

        this._fsLeftContainer = new PIXI.Container();
        this._fsLeftContainer.name = "fsLeftContainer";
        this._fsLeftContainer.visible = false;
        this._fsLeftContainer.addChild(this._fsLeftPortrait, this._fsLeftText);

        this._sphinxLayer.addChildAt(this._sphinxStone, 0);
        this._sphinxLayer.addChild(attachedRopeLeft, attachedRopeRight, this._fsLeftContainer);
        this.createPulleyGraphics();
        this.createAnimations();
    }

    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);

        this._deviceOrientation = resizeData.orientation;

        if (resizeData.orientation === Orientation.PORTRAIT) {
            if (this.isBonusGame()) {
                this._fsLeftContainer.visible = true;
            }else{
                this._fsLeftContainer.visible = false;
            }
            this._pulleyContainerLeft.position.set(30, -200);
            this._pulleyContainerRight.position.set(690, -200);

            this._pulleyContainerLeft.scale.set(0.7);
            this._pulleyContainerRight.scale.set(-0.7, 0.7);


        } else {
            this._fsLeftContainer.visible = false;

            this._pulleyContainerLeft.position.set(-250, 145);
            this._pulleyContainerRight.position.set(968, 145);

            this._pulleyContainerLeft.scale.set(0.8);
            this._pulleyContainerRight.scale.set(-0.8, 0.8);
        }
        this.toggleBottomRopes(resizeData.orientation);
    }

    private onDataParsed(data: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData): void {
        this._gameData = data;
    }

    private onNewRound(): void {
        //not more then 5
        this.rotatePulleys(2, true);
        this.updateSpinsLeft();
    }

    private onStonePress(rowCount: number): void {
        this.rotatePulleys();
    }

    private createAnimations(): void {
        let stoneFlakeAnim_1 = new TimelineSprite(GameResources.getTextures("stoneFlakes"), 30),
            stoneFlakeAnim_2 = new TimelineSprite(GameResources.getTextures("stoneFlakes"), 30),
            stoneFlakeAnim_3 = new TimelineSprite(GameResources.getTextures("stoneFlakes"), 30),
            stoneFlakeAnim_4 = new TimelineSprite(GameResources.getTextures("stoneFlakes"), 30),
            stoneFlakeAnim_5 = new TimelineSprite(GameResources.getTextures("stoneFlakes"), 30);

        stoneFlakeAnim_1.name = "stoneAnim_1";
        stoneFlakeAnim_2.name = "stoneAnim_2";
        stoneFlakeAnim_3.name = "stoneAnim_3";
        stoneFlakeAnim_4.name = "stoneAnim_4";
        stoneFlakeAnim_5.name = "stoneAnim_5";

        stoneFlakeAnim_1.visible = false;
        stoneFlakeAnim_2.visible = false;
        stoneFlakeAnim_3.visible = false;
        stoneFlakeAnim_4.visible = false;
        stoneFlakeAnim_5.visible = false;

        stoneFlakeAnim_1.anchor.set(0.5, 0.5);
        stoneFlakeAnim_2.anchor.set(0.5, 0.5);
        stoneFlakeAnim_3.anchor.set(0.5, 0.5);
        stoneFlakeAnim_4.anchor.set(0.5, 0.5);
        stoneFlakeAnim_5.anchor.set(0.5, 0.5);

        stoneFlakeAnim_1.position.set(0, 60);
        stoneFlakeAnim_2.position.set(170, 60);
        stoneFlakeAnim_3.position.set(360, 60);
        stoneFlakeAnim_4.position.set(500, 60);
        stoneFlakeAnim_5.position.set(660, 60);

        let smokeOutBL = new TimelineSprite(GameResources.getTextures("smokeOut"), 30),
            smokeOutBR = new TimelineSprite(GameResources.getTextures("smokeOut"), 30);

        smokeOutBL.name = "smokeOutBL";
        smokeOutBR.name = "smokeOutBR";

        smokeOutBL.visible = false;
        smokeOutBR.visible = false;

        smokeOutBR.scale.x = -1;

        smokeOutBL.anchor.set(0.5, 0.5);
        smokeOutBR.anchor.set(0.5, 0.5);

        smokeOutBL.position.set(0, 50);
        smokeOutBR.position.set(720, 50);

        let smokeUpAnim_1 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_2 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_3 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_4 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_5 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_6 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_7 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_8 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_9 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_10 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_11 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30),
            smokeUpAnim_12 = new TimelineSprite(GameResources.getTextures("smokeUp"), 30);

        smokeUpAnim_1.name = "smokeUpAnim_1";
        smokeUpAnim_2.name = "smokeUpAnim_2";
        smokeUpAnim_3.name = "smokeUpAnim_3";
        smokeUpAnim_4.name = "smokeUpAnim_4";
        smokeUpAnim_5.name = "smokeUpAnim_5";
        smokeUpAnim_6.name = "smokeUpAnim_6";
        smokeUpAnim_7.name = "smokeUpAnim_7";
        smokeUpAnim_8.name = "smokeUpAnim_8";
        smokeUpAnim_9.name = "smokeUpAnim_9";
        smokeUpAnim_10.name = "smokeUpAnim_10";
        smokeUpAnim_11.name = "smokeUpAnim_11";
        smokeUpAnim_12.name = "smokeUpAnim_12";

        smokeUpAnim_1.visible = false;
        smokeUpAnim_2.visible = false;
        smokeUpAnim_3.visible = false;
        smokeUpAnim_4.visible = false;
        smokeUpAnim_5.visible = false;
        smokeUpAnim_6.visible = false;
        smokeUpAnim_7.visible = false;
        smokeUpAnim_8.visible = false;
        smokeUpAnim_9.visible = false;
        smokeUpAnim_10.visible = false;
        smokeUpAnim_11.visible = false;
        smokeUpAnim_12.visible = false;

        smokeUpAnim_1.anchor.set(0.5, 0.5);
        smokeUpAnim_2.anchor.set(0.5, 0.5);
        smokeUpAnim_3.anchor.set(0.5, 0.5);
        smokeUpAnim_4.anchor.set(0.5, 0.5);
        smokeUpAnim_5.anchor.set(0.5, 0.5);
        smokeUpAnim_6.anchor.set(0.5, 0.5);
        smokeUpAnim_7.anchor.set(0.5, 0.5);
        smokeUpAnim_8.anchor.set(0.5, 0.5);
        smokeUpAnim_9.anchor.set(0.5, 0.5);
        smokeUpAnim_10.anchor.set(0.5, 0.5);
        smokeUpAnim_11.anchor.set(0.5, 0.5);
        smokeUpAnim_12.anchor.set(0.5, 0.5);

        smokeUpAnim_1.scale.set(-3, 1.5);
        smokeUpAnim_2.scale.set(2, 0.75);
        smokeUpAnim_3.scale.set(-2, 0.57);
        smokeUpAnim_4.scale.set(3, 0.75);
        smokeUpAnim_5.scale.set(-1.5, 0.75);
        smokeUpAnim_6.scale.set(2, 1);

        smokeUpAnim_7.scale.set(-3, 1.5);
        smokeUpAnim_8.scale.set(2, 0.75);
        smokeUpAnim_9.scale.set(-2, 0.57);
        smokeUpAnim_10.scale.set(3, 0.75);
        smokeUpAnim_11.scale.set(-1.5, 0.75);
        smokeUpAnim_12.scale.set(2, 1);

        smokeUpAnim_1.position.set(50, 50);
        smokeUpAnim_2.position.set(200, 85);
        smokeUpAnim_3.position.set(360, 90);
        smokeUpAnim_4.position.set(420, 90);
        smokeUpAnim_5.position.set(600, 85);
        smokeUpAnim_6.position.set(720, 75);

        smokeUpAnim_7.position.set(50, 50);
        smokeUpAnim_8.position.set(200, 85);
        smokeUpAnim_9.position.set(360, 90);
        smokeUpAnim_10.position.set(420, 90);
        smokeUpAnim_11.position.set(600, 85);
        smokeUpAnim_12.position.set(720, 75);

        this._stoneFlakeContainer.position.set(0, 600);

        let smokeAnimOnSphinx:PIXI.Container = new PIXI.Container();
        smokeAnimOnSphinx.name = "smokeAnimations";
        smokeAnimOnSphinx.position.set(0, 100);
        smokeAnimOnSphinx.addChild(smokeUpAnim_7, smokeUpAnim_8, smokeUpAnim_9, smokeUpAnim_10, smokeUpAnim_11, smokeUpAnim_12);

        this._animOnSphinxContainer.addChild(smokeAnimOnSphinx);

        this._stoneFlakeContainer.addChild(stoneFlakeAnim_1, stoneFlakeAnim_2, stoneFlakeAnim_3, stoneFlakeAnim_4, stoneFlakeAnim_5, smokeOutBL, smokeOutBR, smokeUpAnim_1, smokeUpAnim_2, smokeUpAnim_3, smokeUpAnim_4, smokeUpAnim_5, smokeUpAnim_6);
        this._sphinxLayer.addChild(this._animOnSphinxContainer);
        this._pulleyLayer.addChild(this._stoneFlakeContainer);
    }

    private createPulleyGraphics():void {
        //left side
        this._pulleyContainerLeft.name = this.PULLEY_CONT_LEFT;

        const ropeTL:PIXI.Sprite = new PIXI.Sprite(this.ropeTextureTop);
        ropeTL.name = this.ROPE_TOP;
        ropeTL.anchor.set(0.5);
        ropeTL.position.set(-240, -280);

        const ropeBL:PIXI.Sprite = new PIXI.Sprite(this.ropeTextureBottom);
        ropeBL.name = this.ROPE_BOTTOM;
        ropeBL.anchor.set(0.5);
        ropeBL.position.set(-190, 260);

        const pulleyImgL:PIXI.Sprite = new PIXI.Sprite(this.pulleyTexture);
        pulleyImgL.name = this.PULLEY;
        pulleyImgL.anchor.set(0.5);
        pulleyImgL.position.set(0, 0);

        this._pulleyContainerLeft.addChild(ropeTL, ropeBL, pulleyImgL);

        //right side
        this._pulleyContainerRight.name = this.PULLEY_CONT_RIGHT;
        this._pulleyContainerRight.scale.x = -1;

        const ropeTR:PIXI.Sprite = new PIXI.Sprite(this.ropeTextureTop);
        ropeTR.name = this.ROPE_TOP;
        ropeTR.anchor.set(0.5);
        ropeTR.position.set(-240, -280);

        const ropeBR:PIXI.Sprite = new PIXI.Sprite(this.ropeTextureBottom);
        ropeBR.name = this.ROPE_BOTTOM;
        ropeBR.anchor.set(0.5);
        ropeBR.position.set(-190, 260);

        const pulleyImgR:PIXI.Sprite = new PIXI.Sprite(this.pulleyTexture);
        pulleyImgR.name = this.PULLEY;
        pulleyImgR.anchor.set(0.5);
        pulleyImgR.position.set(0, 0);

        this._pulleyContainerRight.addChild(ropeTR, ropeBR, pulleyImgR);

        //adding to layer
        this._pulleyLayer.addChild(this._pulleyContainerLeft, this._pulleyContainerRight);
    }

    private rotatePulleys( degree?: number,isNewRound : boolean = false): void {
        let deg: number = degree ? degree : 6,
            pressDuration: number = deg < 6 ? 0.1 : this._stonePressDuration,
            liftDuration: number = deg < 6 ? 0.1 : this._stoneLiftDuration;

        if(GameSetting.isFastSpin){
            pressDuration = TombOfAkhenatenGameConfig.instance.STONE_PRESS_DURATION;
            liftDuration = TombOfAkhenatenGameConfig.instance.STONE_LIFT_DURATION;
        }

        isNewRound && SoundManager.playRopeRotateSound();
        for (let i: number = 0; i < this._pulleyLayer.children.length; i++) {
            let pulleyContainer = this._pulleyLayer.children[i] as PIXI.Container;

            (<PIXI.Sprite[]>pulleyContainer.children).forEach((sprite: PIXI.Sprite) => {
                if (sprite.name && sprite.name.indexOf("pulley") > -1) {
                    TweenLite.fromTo(sprite, pressDuration, {rotation: sprite.rotation}, {
                        delay: this._stonePressStartDelay,
                        rotation: sprite.rotation + deg,
                        ease: Linear.easeNone,
                        onComplete: () => {
                            TweenLite.fromTo(sprite, liftDuration, {rotation: sprite.rotation}, {
                                delay: this._stoneLiftStartDelay,
                                rotation: sprite.rotation - deg,
                                ease: Linear.easeNone
                            });
                        }
                    });
                }

                if (sprite.name && sprite.name.indexOf("ope") > -1) {
                    let oldPosX: number = (sprite.name.indexOf("Top") > -1) ? -240 : -190,
                        oldPosY: number = (sprite.name.indexOf("Bottom") > -1) ? 260 : -280,
                        displacementX: number = (sprite.name.indexOf("Top") > -1) ? 50 : -50,
                        displacementY: number = (sprite.name.indexOf("Bottom") > -1) ? 35 : 35;

                    TweenLite.fromTo(sprite, pressDuration, {
                        x: oldPosX,
                        y: oldPosY
                    }, {
                        delay: this._stonePressStartDelay,
                        x: oldPosX + displacementX,
                        y: oldPosY + displacementY,
                        ease: Linear.easeNone,
                        onComplete: () => {
                            TweenLite.fromTo(sprite, liftDuration, {
                                x: oldPosX + displacementX,
                                y: oldPosY + displacementY,
                            }, {
                                delay: this._stoneLiftStartDelay,
                                x: oldPosX,
                                y: oldPosY,
                                ease: Linear.easeNone
                            });
                        }
                    });
                }

                if (!degree && ((sprite.name && sprite.name.indexOf("stoneAnim_") > -1) ||
                    (sprite.name && sprite.name.indexOf("smokeOut") > -1) ||
                    (sprite.name && sprite.name.indexOf("smokeUp") > -1))) {
                    (sprite as TimelineSprite).getAnimationAutoShowHide(true, true);
                }
            });
        }

        for (let i: number = 0; i < this._sphinxLayer.children.length; i++) {
            if (!degree && this._sphinxLayer.children[i].name === "sphinxAnimation") {
                let sphinxContainer = this._sphinxLayer.children[i] as PIXI.Container;

                for (let j: number = 0; j < sphinxContainer.children.length; j++) {
                    let smokeContainer = sphinxContainer.children[j] as PIXI.Container;

                    (<PIXI.Sprite[]>smokeContainer.children).forEach((sprite: PIXI.Sprite) => {
                        if ((sprite.name && sprite.name.indexOf("smokeUp") > -1)) {
                            (sprite as TimelineSprite).getAnimationAutoShowHide(true, true);
                        }
                    });
                }
            }
        }
    }

    private toggleBottomRopes(deviceOrientation: Orientation): void {
        let showRopes: boolean = false;
        if (deviceOrientation === Orientation.LANDSCAPE) {
            showRopes = true;
        }
        (<PIXI.Sprite[]>(this._pulleyContainerLeft as PIXI.Container).children).forEach((sprite: PIXI.Sprite) => {
            if (sprite.name && sprite.name === this.ROPE_BOTTOM) {
                sprite.visible = showRopes;
            }
        });
        (<PIXI.Sprite[]>(this._pulleyContainerRight as PIXI.Container).children).forEach((sprite: PIXI.Sprite) => {
            if (sprite.name && sprite.name === this.ROPE_BOTTOM) {
                sprite.visible = showRopes;
            }
        });
    }

    private onModeChange(mode: string): void {
        this._gameMode = mode;
        if (this.isBonusGame()) {

            if(TombOfAkhenatenGameConfig.instance.GAME_RESTORED ||
                ((TombOfAkhenatenGameConfig.instance.BONUS_GAME_MODES.indexOf(this._gameData.mode) === -1) &&
                    (TombOfAkhenatenGameConfig.instance.BONUS_GAME_MODES.indexOf(this._gameData.nextMode) > -1))){
                this.updateSpinsLeft(this._gameData.freespinsLeft);
            }

            if(this._deviceOrientation === Orientation.PORTRAIT){
                this._fsLeftContainer.visible = true;
            }else{
                this._fsLeftContainer.visible = false;
            }
        } else {
            this._fsLeftContainer.visible = false;
        }
    }

    private isBonusGame(): boolean {
        let flag = false;
        if (TombOfAkhenatenGameConfig.instance.BONUS_GAME_MODES.indexOf(this._gameMode) > -1) {
            flag = true;
        }
        return flag;
    }

    private updateSpinsLeft(fsCount?: number): void{
        let fsLeftCount = fsCount ? fsCount : this._gameData.freespinsLeft - 1;
        if(fsLeftCount < 0){
            fsLeftCount = 0;
        }
        this._fsLeftText.text = fsLeftCount.toString();
        this.applyScaling();
    }

    private applyScaling(): void {
        let textW = this._fsLeftText.textWidth,
            posX: number = 0,
            scaleValue: number = 1;

        if (textW > 70) {
            scaleValue = 0.8;
            posX = 42;
        } else if (textW > 35) {
            scaleValue = 1;
            posX = 54;
        } else {
            scaleValue = 1;
            posX = 65;
        }
        this._fsLeftText.scale.set(scaleValue);
        this._fsLeftText.position.set(posX, -57);
    }
}